import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  manageInternalUserFlagPromise,
  type ManageInternalUserFlagPromiseParams,
} from 'src/utils/api/internal_user_flag';
import { useNotification } from 'src/hooks/use-notification';

export const useGetInternalUserFlag = (email: string) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['internal_user_flag', email],
    queryFn: ({ signal }) =>
      manageInternalUserFlagPromise(
        {
          action: 'RETRIEVE',
          user_email: email,
        },
        signal
      ),
    enabled: !!email,
  });

  return { isInternalUser: Boolean(data), isLoading, error };
};

export const useAddInternalUserFlag = () => {
  const { mutateAsync, ...rest } = useMutation({
    mutationFn: (params: ManageInternalUserFlagPromiseParams) => manageInternalUserFlagPromise(params, undefined),
  });

  const queryClient = useQueryClient();

  const { enqueueErrorNotification, enqueueSuccessNotification } = useNotification();

  const addInternalUserFlagAsync = async (email: string) => {
    const res = await mutateAsync({
      action: 'ADD',
      user_email: email,
    });
    await queryClient.invalidateQueries({
      queryKey: ['internal_user_flag', email],
    });

    if (res) {
      enqueueSuccessNotification('Successfully added internal user flag');
    } else {
      enqueueErrorNotification('Failed to add internal user flag');
    }

    return res;
  };
  return {
    addInternalUserFlagAsync,
    ...rest,
  };
};

export const useRemoveInternalUserFlag = () => {
  const { mutateAsync, ...rest } = useMutation({
    mutationFn: (params: ManageInternalUserFlagPromiseParams) => manageInternalUserFlagPromise(params, undefined),
  });

  const queryClient = useQueryClient();

  const { enqueueErrorNotification, enqueueSuccessNotification } = useNotification();

  const removeInternalUserFlagAsync = async (email: string) => {
    const res = await mutateAsync({
      action: 'DELETE',
      user_email: email,
    });

    await queryClient.invalidateQueries({
      queryKey: ['internal_user_flag', email],
    });

    if (res) {
      enqueueSuccessNotification('Successfully removed internal user flag');
    } else {
      enqueueErrorNotification('Failed to remove internal user flag');
    }

    return res;
  };
  return {
    removeInternalUserFlagAsync,
    ...rest,
  };
};
